import React from 'react';
import BaseTile from '../../../components/Tile/BaseTile';
import ContentSection, { ContentRow, ContentColumn } from '../../../components/layout/ContentSection';
import { useCounts } from './hooks/useCounts';
import { useAutoLoad } from '../../../hooks/useAutoLoad';
import { formatGermanNumber, formatCompactNumber } from '../../../utils/numberFormat';
import { useAutoFitFontSize } from '../_shared/useAutoFitFontSize';

const STAT_NUMBER_MAX_PX = 36; // was text-4xl
const STAT_NUMBER_MIN_PX = 16;

const StatNumber = ({ value }) => {
  const text = typeof value === 'number' ? formatGermanNumber(value, { decimals: 0 }) : '—';
  const compactText = typeof value === 'number' ? formatCompactNumber(value) : undefined;
  const { ref, fontSizePx, displayText, isCompact } = useAutoFitFontSize({
    text,
    maxPx: STAT_NUMBER_MAX_PX,
    minPx: STAT_NUMBER_MIN_PX,
    compactText,
  });

  return (
    <div
      ref={ref}
      className="max-w-full truncate font-bold text-primary-600 tracking-tight"
      style={{ fontSize: `${fontSizePx}px` }}
      title={isCompact ? text : undefined}
    >
      {displayText}
    </div>
  );
};

const CountsTiles = ({ activeFile, single, onRefresh }) => {
  const { data, loading, error, refresh } = useCounts(activeFile);
  const hasData = !!data;

  // Auto-load data when persistent cache is enabled
  useAutoLoad(refresh, [activeFile], hasData);

  // Provide refresh function to parent (GridComponent)
  React.useEffect(() => {
    if (onRefresh) {
      onRefresh(refresh);
    }
  }, [onRefresh, refresh]);

  // Single-tile rendering for grid usage
  if (single) {
    const renderSingle = (key) => {
      switch (key) {
        case 'totalEvents':
          return (
            <BaseTile loading={loading} error={error} minHeight="sm">
              <div className="flex items-center justify-center h-full py-4">
                <StatNumber value={data?.totalEvents} />
              </div>
            </BaseTile>
          );
        case 'totalObjects':
          return (
            <BaseTile loading={loading} error={error} minHeight="sm">
              <div className="flex items-center justify-center h-full py-4">
                <StatNumber value={data?.totalObjects} />
              </div>
            </BaseTile>
          );
        case 'objectTypeCount':
          return (
            <BaseTile loading={loading} error={error} minHeight="sm">
              <div className="flex items-center justify-center h-full py-4">
                <StatNumber value={data?.objectTypeCount} />
              </div>
            </BaseTile>
          );
        case 'eventTypeCount':
          return (
            <BaseTile loading={loading} error={error} minHeight="sm">
              <div className="flex items-center justify-center h-full py-4">
                <StatNumber value={data?.eventTypeCount} />
              </div>
            </BaseTile>
          );
        case 'timeSpan':
          return (
            <BaseTile loading={loading} error={error} minHeight="sm">
              <div className="flex flex-col items-center justify-center h-full py-3 gap-2">
                <div className="flex items-center justify-center gap-3 font-semibold text-primary-600">
                  <span className="font-mono text-xl">
                    {data?.timeSpan?.start_timestamp ? data.timeSpan.start_timestamp.split('T')[0] : '—'}
                  </span>
                  <svg className="w-8 h-8 text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                  </svg>
                  <span className="font-mono text-xl">
                    {data?.timeSpan?.end_timestamp ? data.timeSpan.end_timestamp.split('T')[0] : '—'}
                  </span>
                </div>
                <div className="text-lg font-semibold text-primary-600 text-center">
                  {data?.timeSpan?.duration_days != null ? `(${data.timeSpan.duration_days} days)` : ''}
                </div>
              </div>
            </BaseTile>
          );
        default:
          return null;
      }
    };

    return renderSingle(single);
  }

  // Original group rendering
  return (
    <ContentSection spacing="lg">
      <ContentRow gap="lg" responsive>
        <ContentColumn flex="1" minWidth="sm">
          <BaseTile
            title="Total Events"
            loading={loading}
            error={error}
            minHeight="sm"
          >
            <div className="flex items-center justify-center h-full py-4">
              <StatNumber value={data?.totalEvents} />
            </div>
          </BaseTile>
        </ContentColumn>
        <ContentColumn flex="1" minWidth="sm">
          <BaseTile
            title="Total Objects"
            loading={loading}
            error={error}
            minHeight="sm"
          >
            <div className="flex items-center justify-center h-full py-4">
              <StatNumber value={data?.totalObjects} />
            </div>
          </BaseTile>
        </ContentColumn>
        <ContentColumn flex="1" minWidth="sm">
          <BaseTile
            title="Object Types"
            loading={loading}
            error={error}
            minHeight="sm"
          >
            <div className="flex items-center justify-center h-full py-4">
              <StatNumber value={data?.objectTypeCount} />
            </div>
          </BaseTile>
        </ContentColumn>
        <ContentColumn flex="1" minWidth="sm">
          <BaseTile
            title="Event Types"
            loading={loading}
            error={error}
            minHeight="sm"
          >
            <div className="flex items-center justify-center h-full py-4">
              <StatNumber value={data?.eventTypeCount} />
            </div>
          </BaseTile>
        </ContentColumn>
        <ContentColumn flex="1" minWidth="sm">
          <BaseTile
            title="Time Span"
            loading={loading}
            error={error}
            minHeight="sm"
          >
            <div className="flex flex-col items-center justify-center h-full py-3 gap-1">
              <div className="text-sm font-semibold text-gray-800 text-center">
                {data?.timeSpan?.pretty || '—'}
              </div>
              <div className="text-xs text-gray-500">
                {data?.timeSpan?.start_timestamp ? data.timeSpan.start_timestamp.split('T')[0] : '—'}
                {' → '}
                {data?.timeSpan?.end_timestamp ? data.timeSpan.end_timestamp.split('T')[0] : '—'}
              </div>
            </div>
          </BaseTile>
        </ContentColumn>
      </ContentRow>
    </ContentSection>
  );
};

export default CountsTiles;
